import { debugLog, debugWarn } from './debugUtils';
import type { StorePinData } from './storeTypes';
import { DEFAULT_MAP_CONFIG } from '../mapConstants';

export type Point = [number, number];
export type Polygon = Point[];

/**
 * Extracts polygon points from an SVG element.
 * It handles polygons, polylines, rects, and approximates other shapes
 * by sampling points along their path.
 *
 * @param element The SVGGraphicsElement to extract points from.
 * @returns A polygon represented as an array of points.
 */
export function getPolygonFromElement(element: SVGGraphicsElement): Polygon {
    if (element instanceof SVGPolygonElement || element instanceof SVGPolylineElement) {
        const points: Point[] = [];
        for (let i = 0; i < element.points.length; i++) {
            const point = element.points.getItem(i);
            points.push([point.x, point.y]);
        }
        return points;
    }

    if (element instanceof SVGRectElement) {
        const x = element.x.baseVal.value;
        const y = element.y.baseVal.value;
        const width = element.width.baseVal.value;
        const height = element.height.baseVal.value;
        return [
            [x, y],
            [x + width, y],
            [x + width, y + height],
            [x, y + height]
        ];
    }

    // This is a simplification for paths and other shapes.
    // It gets points along the perimeter of the shape.
    if (typeof (element as any).getTotalLength === 'function') {
        const pathLength = (element as any).getTotalLength();
        if (pathLength > 0) {
            const points: Point[] = [];
            // Sample points along the path. The more points, the more accurate.
            // Let's sample at a reasonable interval.
            const step = Math.max(10, pathLength / 50);
            for (let i = 0; i < pathLength; i += step) {
                const p = (element as any).getPointAtLength(i);
                points.push([p.x, p.y]);
            }
            return points;
        }
    }

    // Fallback for shapes where we can't get a path (like <g> or complex ones)
    // or for zero-length paths. We use the bounding box.
    const bbox = element.getBBox();
    const { x, y, width, height } = bbox;
    return [
        [x, y],
        [x + width, y],
        [x + width, y + height],
        [x, y + height]
    ];
}

/**
 * Calculates a single bounding box that encloses all the provided SVG elements.
 * @param elements An array of SVGGraphicsElement to combine.
 * @returns A DOMRect representing the combined bounding box.
 */
export function getCombinedBoundingBox(elements: SVGGraphicsElement[]): DOMRect {
    if (elements.length === 0) {
        return new DOMRect(0, 0, 0, 0);
    }

    let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

    elements.forEach(element => {
        const bbox = element.getBBox();
        minX = Math.min(minX, bbox.x);
        minY = Math.min(minY, bbox.y);
        maxX = Math.max(maxX, bbox.x + bbox.width);
        maxY = Math.max(maxY, bbox.y + bbox.height);
    });

    return new DOMRect(minX, minY, maxX - minX, maxY - minY);
}

/**
 * Function to extract the base ID from a shape ID
 */
export function getBaseShapeId(shapeId: string): string {
  return shapeId.match(/^(.*?)(?:_\d+)?$/)?.[1] || shapeId;
}

/**
 * Function to find all related shapes with the same base ID
 */
export function findRelatedShapes(svgElement: SVGSVGElement, baseId: string): SVGElement[] {
  if (!svgElement) return [];
  
  debugLog(`Finding all shapes related to base ID: ${baseId}`, 'shapeMatching');
  
  const relatedPattern = new RegExp(`^${baseId}(?:_\\d+)?$`);
  const allShapes = svgElement.querySelectorAll('path, polygon, rect, circle, ellipse, line');
  const relatedShapes: SVGElement[] = [];
  
  allShapes.forEach(shape => {
    if (shape.id && relatedPattern.test(shape.id)) {
      relatedShapes.push(shape as SVGElement);
    }
  });
  
  debugLog(`Found ${relatedShapes.length} total related shapes for base ID ${baseId}`, 'shapeMatching');
  return relatedShapes;
}

/**
 * Function to find the container element holding store shapes
 */
export function findStoreShapesContainer(
  svgElement: SVGSVGElement, 
  storeShapesContainerId: string | null = null,
  foundContainerIds: Set<string> = new Set<string>()
): Element | null {
  if (!svgElement) {
    debugLog('Cannot find store shapes container: SVG element not found', 'containerDetection');
    return null;
  }
  
  // If a custom container ID is provided, try that first
  if (storeShapesContainerId) {
    const customContainer = svgElement.querySelector(`#${storeShapesContainerId}`);
    if (customContainer) {
      // Check if we've already logged this container
      const containerId = customContainer.id || 'custom-container';
      if (!foundContainerIds.has(containerId)) {
        debugLog(`Using custom container with ID: #${storeShapesContainerId}`, 'containerDetection');
        foundContainerIds.add(containerId);
      }
      return customContainer;
    } else {
      debugWarn(`Custom container ID "${storeShapesContainerId}" was provided but not found in SVG`);
    }
  }
  
  // Simple approach: Find store paths, then get their immediate parent
  const storePattern = /^storePath-[A-Z0-9]+/;
  const storePaths = Array.from(svgElement.querySelectorAll('path')).filter(path => 
    path.id && storePattern.test(path.id)
  );
  
  if (storePaths.length > 0) {
    // Get the parent of the first store path
    const commonParent = storePaths[0].parentElement;
    
    if (commonParent) {
      // Check if we've already logged this container
      const containerId = commonParent.id || `${commonParent.tagName}-parent`;
      if (!foundContainerIds.has(containerId)) {
        debugLog(`Found store paths container: ${commonParent.tagName}${commonParent.id ? ' #' + commonParent.id : ''}`, 'containerDetection');
        foundContainerIds.add(containerId);
      }
      return commonParent;
    }
  }
  
  // Fallback to common IDs if no store paths found
  const commonContainerIds = ['Layer_1', 'stores', 'store-shapes', 'map-content', 'spaces'];
  for (const id of commonContainerIds) {
    const container = svgElement.querySelector(`#${id}`);
    if (container) {
      // Check if we've already logged this container
      if (!foundContainerIds.has(id)) {
        debugLog(`Found container with common ID: #${id}`, 'containerDetection');
        foundContainerIds.add(id);
      }
      return container;
    }
  }
  
  // Last resort: just use the SVG element itself
  const svgId = 'svg-root';
  if (!foundContainerIds.has(svgId)) {
    debugLog('No suitable container found, using the SVG element itself', 'containerDetection');
    foundContainerIds.add(svgId);
  }
  return svgElement;
}

/**
 * Find the SVG shape that contains a given point.
 * This is used to map a store's (x, y) coordinates to a shape on the SVG.
 */
export function findShapeAtPoint(
  svgElement: SVGSVGElement, 
  x: number, 
  y: number
): string | null {
  if (!svgElement) return null;
  
  debugLog(`Finding shape at point (${x}, ${y})`, 'shapeMatching');

  const shapes = svgElement.querySelectorAll('path, polygon, rect, circle, ellipse, line');
  debugLog(`Checking against ${shapes.length} shapes in the SVG`, 'shapeMatching');
  
  const exactMatches: {element: SVGElement, id: string, area: number, baseId?: string}[] = [];
  const proximityMatches: {element: SVGElement, id: string, distance: number, area: number, baseId?: string}[] = [];
  
  shapes.forEach((shape) => {
    const element = shape as SVGElement;
    const shapeId = element.id || '';
    
    if (!shapeId) return;
    
    try {
      if (element instanceof SVGGeometryElement) {
        const point = svgElement.createSVGPoint();
        point.x = x;
        point.y = y;
        
        // Check for exact match first
        if (element.isPointInFill(point)) {
          debugLog(`Point is inside shape: ${shapeId}`, 'shapeMatching');
          
          const baseId = shapeId.match(/^(.*?)(?:_\d+)?$/)?.[1] || shapeId;
          const bbox = (element as SVGGraphicsElement).getBBox();
          const area = bbox.width * bbox.height;
          
          exactMatches.push({
            element, 
            id: shapeId, 
            area,
            baseId: baseId !== shapeId ? baseId : undefined
          });
          return;
        }
      }
      
      // Only check proximity if we don't have exact matches yet
      const bbox = (element as SVGGraphicsElement).getBBox();
      const margin = 2; // Reduced from 5 to 2 pixels for better precision
      
      if (x >= bbox.x - margin && 
          x <= bbox.x + bbox.width + margin && 
          y >= bbox.y - margin && 
          y <= bbox.y + bbox.height + margin) {
        
        const centerX = bbox.x + bbox.width/2;
        const centerY = bbox.y + bbox.height/2;
        const distance = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
        const area = bbox.width * bbox.height;
        
        const baseId = shapeId.match(/^(.*?)(?:_\d+)?$/)?.[1] || shapeId;
        
        debugLog(`Point is near shape: ${shapeId}, distance: ${distance.toFixed(2)}, area: ${area.toFixed(2)}, baseId: ${baseId}`, 'shapeMatching');
        
        proximityMatches.push({
          element, 
          id: shapeId, 
          distance,
          area,
          baseId: baseId !== shapeId ? baseId : undefined
        });
      }
    } catch (e) {
      debugWarn(`Error checking shape ${shapeId}:`, e);
    }
  });
  
  // Prioritize exact matches first
  if (exactMatches.length > 0) {
    // If we have exact matches, pick the smallest one (likely the most specific)
    exactMatches.sort((a, b) => a.area - b.area);
    const closest = exactMatches[0];
    
    debugLog(`Found ${exactMatches.length} exact matches, selected smallest: ${closest.id}, area: ${closest.area.toFixed(2)}`, 'shapeMatching');
    
    if (closest.baseId) {
      debugLog(`This shape is part of a multi-part store with base ID: ${closest.baseId}`, 'shapeMatching');
    }
    
    return closest.id;
  }
  
  // Fall back to proximity matches only if no exact matches
  if (proximityMatches.length > 0) {
    // For proximity matches, prioritize by distance first, then by smaller area as tiebreaker
    proximityMatches.sort((a, b) => {
      const distanceDiff = a.distance - b.distance;
      if (Math.abs(distanceDiff) < 1) { // If distances are very close (within 1px)
        return a.area - b.area; // Prefer smaller area
      }
      return distanceDiff;
    });
    
    const closest = proximityMatches[0];
    
    debugLog(`Found ${proximityMatches.length} proximity matches, selected: ${closest.id}, distance: ${closest.distance.toFixed(2)}, area: ${closest.area.toFixed(2)}`, 'shapeMatching');
    
    if (closest.baseId) {
      debugLog(`This shape is part of a multi-part store with base ID: ${closest.baseId}`, 'shapeMatching');
    }
    
    return closest.id;
  }
  
  debugLog(`No shape found at point (${x}, ${y})`, 'shapeMatching');
  
  return null;
}

/**
 * Highlight a specific shape
 */
export function highlightShape(
  svgElement: SVGSVGElement,
  shapeId: string, 
  pin?: StorePinData,
  highlightColor: string = DEFAULT_MAP_CONFIG.shapeHighlighting.fillColor,
  highlightStrokeColor: string = DEFAULT_MAP_CONFIG.shapeHighlighting.strokeColor,
  highlightStrokeWidth: number = parseFloat(DEFAULT_MAP_CONFIG.shapeHighlighting.strokeWidth),
  highlightOpacity: number = DEFAULT_MAP_CONFIG.shapeHighlighting.opacity
) {
  if (!svgElement) return;
  
  try {
    // Remove any existing highlight overlays first to prevent stacking
    const existingOverlays = svgElement.querySelectorAll(`.shape-highlight-overlay[data-for-shape="${shapeId}"]`);
    existingOverlays.forEach(overlay => {
      overlay.parentNode?.removeChild(overlay);
    });
    
    // If we have a pin with related shapes, highlight them all
    if (pin && pin.relatedShapeIds && pin.relatedShapeIds.length > 0) {
      debugLog(`Highlighting all ${pin.relatedShapeIds.length} shapes for multi-part store`, 'shapeMatching');
      pin.relatedShapeIds.forEach(relatedId => {
        // Skip if this is already highlighted to prevent overlay stacking
        const relatedShape = svgElement.querySelector(`#${relatedId}`);
        if (relatedShape && !relatedShape.classList.contains('highlighted-shape')) {
          highlightSingleShape(svgElement, relatedId, highlightColor, highlightStrokeColor, highlightStrokeWidth, highlightOpacity);
        }
      });
    } else {
      // If we don't have the pin or it's a single shape, check if it's part of a multi-part store
      const baseId = getBaseShapeId(shapeId);
      
      if (baseId !== shapeId) {
        // This is a multi-part store, find and highlight all related shapes
        debugLog(`Multi-part store detected. Base ID: ${baseId}`, 'shapeMatching');
        findRelatedShapes(svgElement, baseId).forEach(shape => {
          // Skip if this is already highlighted to prevent overlay stacking
          if (!shape.classList.contains('highlighted-shape')) {
            highlightSingleShape(svgElement, shape.id, highlightColor, highlightStrokeColor, highlightStrokeWidth, highlightOpacity);
          }
        });
      } else {
        // Single shape, just highlight it if not already highlighted
        const shape = svgElement.querySelector(`#${shapeId}`);
        if (shape && !shape.classList.contains('highlighted-shape')) {
          highlightSingleShape(svgElement, shapeId, highlightColor, highlightStrokeColor, highlightStrokeWidth, highlightOpacity);
        }
      }
    }
  } catch (e) {
    debugWarn(`Error highlighting shape(s) for ${shapeId}:`, e);
  }
}

/**
 * Unhighlight a specific shape
 */
export function unhighlightShape(
  svgElement: SVGSVGElement,
  shapeId: string, 
  pin?: StorePinData
) {
  if (!svgElement) return;
  
  try {
    // If we have a pin with related shapes, unhighlight them all
    if (pin && pin.relatedShapeIds && pin.relatedShapeIds.length > 0) {
      debugLog(`Unhighlighting all ${pin.relatedShapeIds.length} shapes for multi-part store`, 'shapeMatching');
      pin.relatedShapeIds.forEach(relatedId => {
        unhighlightSingleShape(svgElement, relatedId);
      });
    } else {
      // If we don't have the pin or it's a single shape, check if it's part of a multi-part store
      const baseId = getBaseShapeId(shapeId);
      
      if (baseId !== shapeId) {
        // This is a multi-part store, find and unhighlight all related shapes
        debugLog(`Multi-part store detected for unhighlight. Base ID: ${baseId}`, 'shapeMatching');
        findRelatedShapes(svgElement, baseId).forEach(shape => {
          unhighlightSingleShape(svgElement, shape.id);
        });
      } else {
        // Single shape, just unhighlight it
        unhighlightSingleShape(svgElement, shapeId);
      }
    }
  } catch (e) {
    debugWarn(`Error unhighlighting shape(s) for ${shapeId}:`, e);
  }
}

/**
 * Helper function to highlight a single shape
 */
function highlightSingleShape(
  svgElement: SVGSVGElement,
  shapeId: string,
  highlightColor: string,
  highlightStrokeColor: string,
  highlightStrokeWidth: number,
  highlightOpacity: number
) {
  if (!svgElement) return;
  
  try {
    const shape = svgElement.querySelector(`#${shapeId}`);
    if (shape) {
      shape.classList.add('highlighted-shape');
      
      // Store original fill to restore later
      if (!shape.hasAttribute('data-original-fill')) {
        const currentFill = shape.getAttribute('fill') || 'none';
        shape.setAttribute('data-original-fill', currentFill);
      }
      
      // Store original stroke properties
      if (!shape.hasAttribute('data-original-stroke')) {
        const currentStroke = shape.getAttribute('stroke') || 'none';
        shape.setAttribute('data-original-stroke', currentStroke);
      }
      
      if (!shape.hasAttribute('data-original-stroke-width')) {
        const currentStrokeWidth = shape.getAttribute('stroke-width') || '1';
        shape.setAttribute('data-original-stroke-width', currentStrokeWidth);
      }
      
      // Create a semi-transparent overlay effect rather than changing the original style completely
      const overlay = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      
      // Copy the path data or shape attributes to create an exact overlay
      let overlayPath = '';
      
      if (shape.tagName.toLowerCase() === 'path') {
        overlayPath = shape.getAttribute('d') || '';
        overlay.setAttribute('d', overlayPath);
      } else if (shape.tagName.toLowerCase() === 'rect') {
        // For rectangles, create a path that matches the rectangle
        const x = parseFloat(shape.getAttribute('x') || '0');
        const y = parseFloat(shape.getAttribute('y') || '0');
        const width = parseFloat(shape.getAttribute('width') || '0');
        const height = parseFloat(shape.getAttribute('height') || '0');
        
        overlayPath = `M ${x} ${y} L ${x+width} ${y} L ${x+width} ${y+height} L ${x} ${y+height} Z`;
        overlay.setAttribute('d', overlayPath);
      } else {
        // For other shapes, apply highlight directly
        shape.setAttribute('fill', highlightColor);
        shape.setAttribute('stroke', highlightStrokeColor);
        shape.setAttribute('stroke-width', highlightStrokeWidth.toString());
        shape.setAttribute('fill-opacity', highlightOpacity.toString());
        return;
      }
      
      // Style the overlay
      overlay.setAttribute('fill', highlightColor);
      overlay.setAttribute('stroke', highlightStrokeColor);
      overlay.setAttribute('stroke-width', highlightStrokeWidth.toString());
      overlay.setAttribute('fill-opacity', highlightOpacity.toString());
      overlay.setAttribute('pointer-events', 'none');
      overlay.classList.add('shape-highlight-overlay');
      overlay.setAttribute('data-for-shape', shapeId);
      
      // Insert the overlay right after the original shape
      const parent = shape.parentNode;
      if (parent) {
        parent.insertBefore(overlay, shape.nextSibling);
      }
    }
  } catch (e) {
    debugWarn(`Error highlighting single shape ${shapeId}:`, e);
  }
}

/**
 * Helper function to unhighlight a single shape
 */
function unhighlightSingleShape(svgElement: SVGSVGElement, shapeId: string) {
  if (!svgElement) return;
  
  try {
    const shape = svgElement.querySelector(`#${shapeId}`);
    if (shape) {
      shape.classList.remove('highlighted-shape');
      
      const overlays = svgElement.querySelectorAll(`.shape-highlight-overlay[data-for-shape="${shapeId}"]`);
      overlays.forEach(overlay => {
        overlay.parentNode?.removeChild(overlay);
      });
      
      if (['path', 'rect', 'polygon', 'polyline'].indexOf(shape.tagName.toLowerCase()) === -1) {
        const originalFill = shape.getAttribute('data-original-fill');
        if (originalFill) {
          shape.setAttribute('fill', originalFill);
        }
        
        const originalStroke = shape.getAttribute('data-original-stroke');
        if (originalStroke) {
          shape.setAttribute('stroke', originalStroke);
        }
        
        const originalStrokeWidth = shape.getAttribute('data-original-stroke-width');
        if (originalStrokeWidth) {
          shape.setAttribute('stroke-width', originalStrokeWidth);
        }
        
        shape.removeAttribute('fill-opacity');
      }
    }
  } catch (e) {
    debugWarn(`Error unhighlighting single shape ${shapeId}:`, e);
  }
} 